import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Tabs, 
  Tab, 
  Container, 
  Typography, 
  Button, 
  CircularProgress, 
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Badge,
  Chip,
  Avatar,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  useTheme
} from '@mui/material';
import { 
  DateRange as CalendarIcon, 
  People as PeopleIcon, 
  AssignmentTurnedIn as RequestsIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  TrendingUp as TrendingUpIcon,
  Timer as TimerIcon,
  WhatsApp as WhatsAppIcon,
  Message as MessageIcon,
  Campaign as CampaignIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  CloudUpload as UploadIcon,
  Business as BusinessIcon,
  Delete as DeleteIcon,
  ViewWeek as KanbanIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Map as MapIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { api } from '../../services/api';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useAuth } from '../../contexts/AuthContext';
import ExpenseTracker from './ExpenseTracker';
import ReferralAndReviews from '../../components/ReferralAndReviews';
import TestRoutesTab from '../student/TestRoutesTab';

const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`instructor-tabpanel-${index}`}
      aria-labelledby={`instructor-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `instructor-tab-${index}`,
    'aria-controls': `instructor-tabpanel-${index}`,
  };
}

const getImageUrl = (path: string | null) => {
  return api.getImageUrl(path);
};

interface LessonRequest {
  id: number;
  studentName: string;
  lessonDate: string;
  duration: number;
  notes: string;
}

const REGION_GROUPS = [
  {
    name: 'London',
    outcodes: [
      { code: 'BARKING AND DAGENHAM', area: 'Barking and Dagenham, London' },
      { code: 'BARNET', area: 'Barnet, London' },
      { code: 'BEXLEY', area: 'Bexley, London' },
      { code: 'BRENT', area: 'Brent, London' },
      { code: 'BROMLEY', area: 'Bromley, London' },
      { code: 'CAMDEN', area: 'Camden, London' },
      { code: 'CITY OF LONDON', area: 'City of London, London' },
      { code: 'CROYDON', area: 'Croydon, London' },
      { code: 'EALING', area: 'Ealing, London' },
      { code: 'ENFIELD', area: 'Enfield, London' },
      { code: 'GREENWICH', area: 'Greenwich, London' },
      { code: 'HACKNEY', area: 'Hackney, London' },
      { code: 'HAMMERSMITH AND FULHAM', area: 'Hammersmith and Fulham, London' },
      { code: 'HARINGEY', area: 'Haringey, London' },
      { code: 'HARROW', area: 'Harrow, London' },
      { code: 'HAVERING', area: 'Havering, London' },
      { code: 'HILLINGDON', area: 'Hillingdon, London' },
      { code: 'HOUNSLOW', area: 'Hounslow, London' },
      { code: 'ISLINGTON', area: 'Islington, London' },
      { code: 'KENSINGTON AND CHELSEA', area: 'Kensington and Chelsea, London' },
      { code: 'KINGSTON UPON THAMES', area: 'Kingston upon Thames, London' },
      { code: 'LAMBETH', area: 'Lambeth, London' },
      { code: 'LEWISHAM', area: 'Lewisham, London' },
      { code: 'MERTON', area: 'Merton, London' },
      { code: 'NEWHAM', area: 'Newham, London' },
      { code: 'REDBRIDGE', area: 'Redbridge, London' },
      { code: 'RICHMOND UPON THAMES', area: 'Richmond upon Thames, London' },
      { code: 'SOUTHWARK', area: 'Southwark, London' },
      { code: 'SUTTON', area: 'Sutton, London' },
      { code: 'TOWER HAMLETS', area: 'Tower Hamlets, London' },
      { code: 'WALTHAM FOREST', area: 'Waltham Forest, London' },
      { code: 'WANDSWORTH', area: 'Wandsworth, London' },
      { code: 'WESTMINSTER', area: 'Westminster, London' }
    ]
  },
  {
    name: 'Manchester',
    outcodes: [
      { code: 'MANCHESTER CITY CENTRE', area: 'Manchester City Centre, Manchester' },
      { code: 'SALFORD', area: 'Salford, Manchester' },
      { code: 'TRAFFORD', area: 'Trafford, Manchester' },
      { code: 'STOCKPORT', area: 'Stockport, Manchester' },
      { code: 'TAMESIDE', area: 'Tameside, Manchester' },
      { code: 'OLDHAM', area: 'Oldham, Manchester' },
      { code: 'ROCHDALE', area: 'Rochdale, Manchester' },
      { code: 'BURY', area: 'Bury, Manchester' },
      { code: 'BOLTON', area: 'Bolton, Manchester' },
      { code: 'WIGAN', area: 'Wigan, Manchester' }
    ]
  },
  {
    name: 'Birmingham',
    outcodes: [
      { code: 'BIRMINGHAM CITY CENTRE', area: 'Birmingham City Centre, Birmingham' },
      { code: 'SOLIHULL', area: 'Solihull, Birmingham' },
      { code: 'SUTTON COLDFIELD', area: 'Sutton Coldfield, Birmingham' },
      { code: 'EDGBASTON', area: 'Edgbaston, Birmingham' },
      { code: 'HARBORNE', area: 'Harborne, Birmingham' },
      { code: 'DUDLEY', area: 'Dudley, Birmingham' },
      { code: 'SANDWELL', area: 'Sandwell, Birmingham' },
      { code: 'WALSALL', area: 'Walsall, Birmingham' },
      { code: 'WOLVERHAMPTON', area: 'Wolverhampton, Birmingham' },
      { code: 'COVENTRY', area: 'Coventry, Birmingham' }
    ]
  },
  {
    name: 'Liverpool',
    outcodes: [
      { code: 'LIVERPOOL CITY CENTRE', area: 'Liverpool City Centre, Liverpool' },
      { code: 'KNOWSLEY', area: 'Knowsley, Liverpool' },
      { code: 'ST HELENS', area: 'St Helens, Liverpool' },
      { code: 'SEFTON', area: 'Sefton, Liverpool' },
      { code: 'WIRRAL', area: 'Wirral, Liverpool' }
    ]
  },
  {
    name: 'Leeds',
    outcodes: [
      { code: 'LEEDS CITY CENTRE', area: 'Leeds City Centre, Leeds' },
      { code: 'HEADINGLEY', area: 'Headingley, Leeds' },
      { code: 'HORSFORTH', area: 'Horsforth, Leeds' },
      { code: 'WETHERBY', area: 'Wetherby, Leeds' },
      { code: 'PUDSEY', area: 'Pudsey, Leeds' },
      { code: 'BRADFORD', area: 'Bradford, Leeds' },
      { code: 'WAKEFIELD', area: 'Wakefield, Leeds' }
    ]
  },
  {
    name: 'Bristol',
    outcodes: [
      { code: 'BRISTOL CITY CENTRE', area: 'Bristol City Centre, Bristol' },
      { code: 'CLIFTON', area: 'Clifton, Bristol' },
      { code: 'REDLAND', area: 'Redland, Bristol' },
      { code: 'BEDMINSTER', area: 'Bedminster, Bristol' },
      { code: 'BATH', area: 'Bath, Bristol' }
    ]
  },
  {
    name: 'Sheffield',
    outcodes: [
      { code: 'SHEFFIELD CITY CENTRE', area: 'Sheffield City Centre, Sheffield' },
      { code: 'KELHAM ISLAND', area: 'Kelham Island, Sheffield' },
      { code: 'ECCLESALL', area: 'Ecclesall, Sheffield' },
      { code: 'HILLSBOROUGH', area: 'Hillsborough, Sheffield' },
      { code: 'ROTHERHAM', area: 'Rotherham, Sheffield' }
    ]
  },
  {
    name: 'Newcastle',
    outcodes: [
      { code: 'NEWCASTLE CITY CENTRE', area: 'Newcastle City Centre, Newcastle' },
      { code: 'JESMOND', area: 'Jesmond, Newcastle' },
      { code: 'GOSFORTH', area: 'Gosforth, Newcastle' },
      { code: 'SUNDERLAND', area: 'Sunderland, Newcastle' },
      { code: 'GATESHEAD', area: 'Gateshead, Newcastle' }
    ]
  },
  {
    name: 'Nottingham',
    outcodes: [
      { code: 'NOTTINGHAM CITY CENTRE', area: 'Nottingham City Centre, Nottingham' },
      { code: 'WEST BRIDGFORD', area: 'West Bridgford, Nottingham' },
      { code: 'BEESTON', area: 'Beeston, Nottingham' },
      { code: 'DERBY', area: 'Derby, Nottingham' }
    ]
  },
  {
    name: 'Leicester',
    outcodes: [
      { code: 'LEICESTER CITY CENTRE', area: 'Leicester City Centre, Leicester' },
      { code: 'STONEYGATE', area: 'Stoneygate, Leicester' },
      { code: 'OADBY', area: 'Oadby, Leicester' },
      { code: 'LOUGHBOROUGH', area: 'Loughborough, Leicester' }
    ]
  },
  {
    name: 'Southampton',
    outcodes: [
      { code: 'SOUTHAMPTON CITY CENTRE', area: 'Southampton City Centre, Southampton' },
      { code: 'PORTSMOUTH CITY CENTRE', area: 'Portsmouth City Centre, Southampton' },
      { code: 'EASTLEIGH', area: 'Eastleigh, Southampton' },
      { code: 'FAREHAM', area: 'Fareham, Southampton' }
    ]
  },
  {
    name: 'Brighton',
    outcodes: [
      { code: 'BRIGHTON CITY CENTRE', area: 'Brighton City Centre, Brighton' },
      { code: 'HOVE', area: 'Hove, Brighton' },
      { code: 'WORTHING', area: 'Worthing, Brighton' }
    ]
  },
  {
    name: 'Oxford',
    outcodes: [
      { code: 'OXFORD CITY CENTRE', area: 'Oxford City Centre, Oxford' }
    ]
  },
  {
    name: 'Cambridge',
    outcodes: [
      { code: 'CAMBRIDGE CITY CENTRE', area: 'Cambridge City Centre, Cambridge' },
      { code: 'PETERBOROUGH', area: 'Peterborough, Cambridge' }
    ]
  },
  {
    name: 'Edinburgh',
    outcodes: [
      { code: 'EDINBURGH CITY CENTRE', area: 'Edinburgh City Centre, Edinburgh' },
      { code: 'ABERDEEN', area: 'Aberdeen, Edinburgh' },
      { code: 'DUNDEE', area: 'Dundee, Edinburgh' }
    ]
  },
  {
    name: 'Glasgow',
    outcodes: [
      { code: 'GLASGOW CITY CENTRE', area: 'Glasgow City Centre, Glasgow' }
    ]
  },
  {
    name: 'Cardiff',
    outcodes: [
      { code: 'CARDIFF CITY CENTRE', area: 'Cardiff City Centre, Cardiff' },
      { code: 'SWANSEA CITY CENTRE', area: 'Swansea City Centre, Cardiff' },
      { code: 'NEWPORT', area: 'Newport, Cardiff' }
    ]
  },
  {
    name: 'Belfast',
    outcodes: [
      { code: 'BELFAST CITY CENTRE', area: 'Belfast City Centre, Belfast' },
      { code: 'LONDONDERRY', area: 'Londonderry, Belfast' }
    ]
  }
];

const LICENSE_CATEGORIES = [
  { code: 'B', name: 'Category B - Car' },
  { code: 'A', name: 'Category A - Motorcycle' },
  { code: 'C', name: 'Category C - Lorry' },
  { code: 'D', name: 'Category D - Bus' },
  { code: 'BE', name: 'Category B+E - Trailer' }
];

const STAGES = [
  { id: 'NewStarter', label: 'New Starter', color: 'primary.main', lightBg: 'rgba(25, 118, 210, 0.04)', darkBg: 'rgba(25, 118, 210, 0.12)' },
  { id: 'InTraining', label: 'In Training', color: 'warning.main', lightBg: 'rgba(237, 108, 2, 0.04)', darkBg: 'rgba(237, 108, 2, 0.12)' },
  { id: 'MockTestReady', label: 'Mock Test Ready', color: 'secondary.main', lightBg: 'rgba(156, 39, 176, 0.04)', darkBg: 'rgba(156, 39, 176, 0.12)' },
  { id: 'LicensePassed', label: 'License Passed', color: 'success.main', lightBg: 'rgba(46, 125, 50, 0.04)', darkBg: 'rgba(46, 125, 50, 0.12)' }
];

const InstructorDashboard: React.FC = () => {
  const theme = useTheme();
  const [value, setValue] = useState(0);
  const [events, setEvents] = useState<Array<{ id: string; title: string; start: Date; end: Date }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [lessonRequests, setLessonRequests] = useState<LessonRequest[]>([]);
  const [actionInProgress, setActionInProgress] = useState<number | null>(null);
  const [students, setStudents] = useState<any[]>([]);
  const [stats, setStats] = useState({ totalStudents: 0, totalHours: 0, earnings: 0, pendingRequests: 0 });
  const [calendarView, setCalendarView] = useState<any>('week');
  const [calendarDate, setCalendarDate] = useState<Date>(new Date());

  // Custom Message Dialog State
  const [isMsgDialogOpen, setIsMsgDialogOpen] = useState(false);
  const [msgData, setMsgData] = useState({
    studentId: '',
    message: '',
    sendEmail: true,
    sendSms: true
  });
  const [isSendingMsg, setIsSendingMsg] = useState(false);

  // Quotes & Profile states
  const [quotes, setQuotes] = useState<any[]>([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState<any>(null);
  const [isQuoteReplyOpen, setIsQuoteReplyOpen] = useState(false);
  const [quoteReplyMessage, setQuoteReplyMessage] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [replyStatus, setReplyStatus] = useState<'Accepted' | 'Rejected'>('Accepted');

  const [profileBio, setProfileBio] = useState('');
  const [profileRate, setProfileRate] = useState(40);
  const [profileTransmission, setProfileTransmission] = useState('Manual');
  const [profileGender, setProfileGender] = useState('');
  const [profileOutcodes, setProfileOutcodes] = useState<string[]>([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');

  const handleAddDistrict = () => {
    if (!selectedDistrict) return;
    if (!profileOutcodes.includes(selectedDistrict)) {
      setProfileOutcodes(prev => [...prev, selectedDistrict]);
    }
    setSelectedDistrict('');
  };

  const handleRemoveDistrict = (codeToRemove: string) => {
    setProfileOutcodes(prev => prev.filter(code => code !== codeToRemove));
  };

  const getOutcodeAreaName = (code: string) => {
    for (const group of REGION_GROUPS) {
      const found = group.outcodes.find(o => o.code === code);
      if (found) return `${found.area.split(',')[0]} (${group.name})`;
    }
    return code;
  };

  const [profileLicenseTypes, setProfileLicenseTypes] = useState<string[]>(['B']);
  const [profileVehicleImageUrls, setProfileVehicleImageUrls] = useState<string[]>([]);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isUploadingVehicleImage, setIsUploadingVehicleImage] = useState(false);
  const [isUploadingProfileImage, setIsUploadingProfileImage] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);
  const [featuredUntil, setFeaturedUntil] = useState<string | null>(null);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [boosting, setBoosting] = useState(false);

  // Google Calendar Integration states
  const [googleCalendarEmail, setGoogleCalendarEmail] = useState<string | null>(null);
  const [googleCalendarSyncEnabled, setGoogleCalendarSyncEnabled] = useState(false);
  const [calendarSyncLoading, setCalendarSyncLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { hasActiveSubscription, isLoading: isSubscriptionLoading, createPayment } = useSubscription();
  const { user, isLoading: isAuthLoading } = useAuth();

  const getInitials = () => {
    if (!user) return '';
    return `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`.toUpperCase();
  };

  useEffect(() => {
    if (isAuthLoading) return;
    if (!isSubscriptionLoading && !hasActiveSubscription) {
      navigate('/instructor/subscribe');
    } else if (!isSubscriptionLoading) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveSubscription, isSubscriptionLoading, isAuthLoading, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam) {
      if (tabParam === 'profile') setValue(4);
      else if (tabParam === 'expenses') setValue(5);
      else if (tabParam === 'kanban') setValue(6);
      else {
        const val = parseInt(tabParam);
        if (!isNaN(val) && val >= 0 && val <= 6) {
          setValue(val);
        }
      }
    }

    if (params.get('syncSuccess') === 'true') {
      setSuccess('Google Calendar connected successfully!');
      setTimeout(() => setSuccess(null), 5000);
      navigate('/instructor/dashboard?tab=0', { replace: true });
    } else if (params.get('syncError') === 'true') {
      setError('Failed to connect Google Calendar. Please try again.');
      setTimeout(() => setError(null), 5000);
      navigate('/instructor/dashboard?tab=0', { replace: true });
    }
  }, [location.search, navigate]);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [slotsResponse, requestsResponse, studentsResponse, quotesResponse, profileResponse] = await Promise.all([
        api.getInstructorAvailabilitySlots(),
        api.getLessonRequests(),
        api.getStudents(),
        api.getReceivedQuotes(),
        api.getMyInstructorProfile()
      ]);

      const slots = slotsResponse.data.map(slot => ({
        id: slot.id,
        title: slot.status === 'Pending' ? 'Pending Approval' : slot.status === 'Approved' ? 'Approved (Booked)' : slot.status === 'Completed' ? 'Completed' : 'Available',
        start: new Date(slot.startTime),
        end: new Date(slot.endTime),
        status: slot.status || (slot.isBooked ? 'Approved' : 'Available')
      }));
      setEvents(slots);

      const requests = requestsResponse.data;
      setLessonRequests(requests);

      const studentsData = studentsResponse.data || [];
      setStudents(studentsData);

      setQuotes(quotesResponse.data || []);

      if (profileResponse.data) {
        setProfileBio(profileResponse.data.profileInfo || profileResponse.data.bio || '');
        setProfileRate(profileResponse.data.baseHourlyRate || 40);
        setProfileTransmission(profileResponse.data.transmissionType || 'Manual');
        setProfileGender(profileResponse.data.gender || '');
        setProfileOutcodes(profileResponse.data.outcodes || []);
        setProfileLicenseTypes(profileResponse.data.licenseTypes || ['B']);
        setProfileVehicleImageUrls(profileResponse.data.vehicleImageUrls || []);
        setProfileImageUrl(profileResponse.data.profileImageUrl || null);
        setLogoUrl(profileResponse.data.logoUrl || null);
        setIsFeatured(profileResponse.data.isFeatured || false);
        setFeaturedUntil(profileResponse.data.featuredUntil || null);
        setGoogleCalendarEmail(profileResponse.data.googleCalendarEmail || null);
        setGoogleCalendarSyncEnabled(profileResponse.data.googleCalendarSyncEnabled || false);
      }

      // Calculate stats
      const totalStudents = studentsData.length;
      let totalMinutes = 0;
      studentsData.forEach(student => {
        if (student.lessons) {
          student.lessons.forEach((lesson: any) => {
            if (lesson.isCompleted) {
              const durMatch = lesson.description ? lesson.description.match(/\d+/) : null;
              const duration = durMatch ? parseInt(durMatch[0]) : 60;
              totalMinutes += duration;
            }
          });
        }
      });

      const totalHours = Math.round(totalMinutes / 60);
      const hourlyRate = 40; // £40 per hour in the UK
      const earnings = totalHours * hourlyRate;

      setStats({
        totalStudents,
        totalHours,
        earnings,
        pendingRequests: requests.length
      });
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('An error occurred while loading data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchQuotes = async () => {
    try {
      setQuotesLoading(true);
      const res = await api.getReceivedQuotes();
      setQuotes(res.data || []);
    } catch (err) {
      console.error('Error fetching quotes:', err);
    } finally {
      setQuotesLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      setProfileLoading(true);
      const res = await api.getMyInstructorProfile();
      if (res.data) {
        setProfileBio(res.data.profileInfo || res.data.bio || '');
        setProfileRate(res.data.baseHourlyRate || 40);
        setProfileTransmission(res.data.transmissionType || 'Manual');
        setProfileGender(res.data.gender || '');
        setProfileOutcodes(res.data.outcodes || []);
        setProfileLicenseTypes(res.data.licenseTypes || ['B']);
        setProfileVehicleImageUrls(res.data.vehicleImageUrls || []);
        setIsFeatured(res.data.isFeatured || false);
        setFeaturedUntil(res.data.featuredUntil || null);
        setGoogleCalendarEmail(res.data.googleCalendarEmail || null);
        setGoogleCalendarSyncEnabled(res.data.googleCalendarSyncEnabled || false);
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleRespondToQuote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedQuote) return;

    try {
      setIsSubmittingReply(true);
      setError(null);
      await api.respondToQuote(selectedQuote.id, replyStatus, quoteReplyMessage);
      setSuccess(`Request successfully ${replyStatus === 'Accepted' ? 'accepted' : 'rejected'}.`);
      setIsQuoteReplyOpen(false);
      setQuoteReplyMessage('');
      await fetchQuotes();
      await fetchData(); // Refresh students/stats if accepted
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error responding to request:', err);
      setError(err.response?.data?.message || 'Failed to respond to request.');
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsUpdatingProfile(true);
      setError(null);
      await api.updateMyInstructorProfile({
        bio: profileBio,
        baseHourlyRate: profileRate,
        transmissionType: profileTransmission,
        gender: profileGender || null,
        outcodes: profileOutcodes,
        licenseTypes: profileLicenseTypes,
        vehicleImageUrls: profileVehicleImageUrls,
        logoUrl: logoUrl
      });
      setSuccess('Profile updated successfully.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError(err.response?.data?.message || 'Failed to update profile.');
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleBoostProfile = async (days: number, amount: number) => {
    try {
      setBoosting(true);
      setError(null);
      const notes = days === 30 ? 'Promotion:30Days' : 'Promotion:7Days';
      const payment = await createPayment(amount, false, 'PayPal', notes);
      if (payment && payment.approvalUrl) {
        window.location.href = payment.approvalUrl;
      } else {
        throw new Error('Failed to create promotion payment or get approval URL.');
      }
    } catch (err: any) {
      console.error('Error boosting profile:', err);
      setError(err.message || 'Failed to initiate boost profile transaction.');
      setBoosting(false);
    }
  };

  const handleConnectCalendar = async () => {
    try {
      setCalendarSyncLoading(true);
      setError(null);
      const res = await api.connectGoogleCalendar();
      if (res.data && res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err: any) {
      console.error('Error connecting Google Calendar:', err);
      setError('Failed to initiate Google Calendar connection.');
    } finally {
      setCalendarSyncLoading(false);
    }
  };

  const handleToggleCalendarSync = async (checked: boolean) => {
    try {
      setCalendarSyncLoading(true);
      setError(null);
      const res = await api.toggleGoogleCalendarSync(checked);
      if (res.data && res.data.success) {
        setGoogleCalendarSyncEnabled(res.data.enabled);
        setSuccess(res.data.enabled ? 'Google Calendar sync enabled.' : 'Google Calendar sync disabled.');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err: any) {
      console.error('Error toggling Google Calendar Sync:', err);
      setError(err.response?.data?.message || 'Failed to toggle Google Calendar sync.');
    } finally {
      setCalendarSyncLoading(false);
    }
  };

  const handleOutcodeToggle = (code: string) => {
    setProfileOutcodes(prev =>
      prev.includes(code)
        ? prev.filter(c => c !== code)
        : [...prev, code]
    );
  };

  const handleLicenseToggle = (code: string) => {
    setProfileLicenseTypes(prev => {
      if (prev.includes(code)) {
        if (prev.length === 1) {
          setError('You must select at least one license category.');
          setTimeout(() => setError(null), 3000);
          return prev;
        }
        return prev.filter(c => c !== code);
      } else {
        return [...prev, code];
      }
    });
  };

  const handleRegionSelectAll = (outcodesInRegion: string[], forceSelect?: boolean) => {
    setProfileOutcodes(prev => {
      const allSelected = outcodesInRegion.every(code => prev.includes(code));
      const shouldSelect = forceSelect !== undefined ? forceSelect : !allSelected;

      if (shouldSelect) {
        const newCodes = outcodesInRegion.filter(code => !prev.includes(code));
        return [...prev, ...newCodes];
      } else {
        return prev.filter(code => !outcodesInRegion.includes(code));
      }
    });
  };

  const handleVehicleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];

    if (profileVehicleImageUrls.length >= 3) {
      setError('You can upload a maximum of 3 vehicle photos.');
      setTimeout(() => setError(null), 5000);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB.');
      setTimeout(() => setError(null), 5000);
      return;
    }

    try {
      setIsUploadingVehicleImage(true);
      setError(null);
      const res = await api.uploadVehicleImage(file);
      if (res.data && res.data.path) {
        setProfileVehicleImageUrls(prev => [...prev, res.data.path]);
        setSuccess('Vehicle image uploaded successfully. Click Save Profile Changes to persist.');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err: any) {
      console.error('Vehicle image upload failed:', err);
      setError(err.response?.data?.message || 'Failed to upload vehicle image.');
    } finally {
      setIsUploadingVehicleImage(false);
      e.target.value = '';
    }
  };

  const handleDeleteVehicleImage = (indexToRemove: number) => {
    setProfileVehicleImageUrls(prev => prev.filter((_, idx) => idx !== indexToRemove));
    setSuccess('Vehicle image removed from profile. Click Save Profile Changes to persist.');
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleProfileImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploadingProfileImage(true);
      setError(null);
      
      const response = await api.uploadProfileImage(file);
      if (response.data && response.data.path) {
        setProfileImageUrl(response.data.path);
        setSuccess('Profile image uploaded successfully.');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err: any) {
      console.error('Profile image upload failed:', err);
      setError(err.response?.data?.message || 'Failed to upload profile image.');
    } finally {
      setIsUploadingProfileImage(false);
      e.target.value = '';
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploadingLogo(true);
      setError(null);
      
      const response = await api.uploadLogo(file);
      if (response.data && response.data.path) {
        setLogoUrl(response.data.path);
        setSuccess('Logo uploaded successfully. Click Save Profile Changes to persist.');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err: any) {
      console.error('Logo upload failed:', err);
      setError(err.response?.data?.message || 'Failed to upload logo.');
    } finally {
      setIsUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleDeleteQuote = async (quoteId: number) => {
    if (!window.confirm("Are you sure you want to delete this request? This action cannot be undone.")) return;
    try {
      await api.deleteQuote(quoteId);
      setSuccess('Request deleted successfully.');
      setTimeout(() => setSuccess(null), 3000);
      fetchQuotes();
    } catch (err: any) {
      console.error('Error deleting quote:', err);
      setError(err.response?.data?.message || 'Failed to delete request.');
    }
  };

  const fetchAvailabilitySlots = async () => {
    try {
      const response = await api.getInstructorAvailabilitySlots();
      const slots = response.data.map(slot => ({
        id: slot.id,
        title: slot.status === 'Pending' ? 'Pending Approval' : slot.status === 'Approved' ? 'Approved (Booked)' : slot.status === 'Completed' ? 'Completed' : 'Available',
        start: new Date(slot.startTime),
        end: new Date(slot.endTime),
        status: slot.status || (slot.isBooked ? 'Approved' : 'Available')
      }));
      setEvents(slots);
    } catch (err) {
      console.error('Error fetching slots:', err);
    }
  };

  const fetchLessonRequests = async () => {
    try {
      const response = await api.getLessonRequests();
      setLessonRequests(response.data);
    } catch (err) {
      console.error('Error fetching lesson requests:', err);
    }
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleApproveRequest = async (id: number) => {
    try {
      setActionInProgress(id);
      setError(null);
      await api.approveLesson(id);
      setSuccess('Lesson request successfully approved.');
      setLessonRequests(prev => prev.filter(req => req.id !== id));
      await fetchAvailabilitySlots();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error approving request:', err);
      setError(err.response?.data?.message || 'An error occurred while approving the lesson.');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleRejectRequest = async (id: number) => {
    try {
      setActionInProgress(id);
      setError(null);
      await api.rejectLesson(id);
      setSuccess('Lesson request rejected.');
      setLessonRequests(prev => prev.filter(req => req.id !== id));
      await fetchAvailabilitySlots();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error rejecting request:', err);
      setError(err.response?.data?.message || 'An error occurred while rejecting the lesson.');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleOpenMessageDialog = (studentId: string = '') => {
    setMsgData({
      studentId,
      message: '',
      sendEmail: true,
      sendSms: true
    });
    setIsMsgDialogOpen(true);
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!msgData.message.trim()) return;

    try {
      setIsSendingMsg(true);
      setError(null);
      await api.sendMessageToStudents({
        studentId: msgData.studentId !== '' ? msgData.studentId : undefined,
        message: msgData.message,
        sendEmail: msgData.sendEmail,
        sendSms: msgData.sendSms
      });
      setSuccess('Message/Announcement successfully sent.');
      setIsMsgDialogOpen(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error sending message:', err);
      setError(err.response?.data?.message || 'Failed to send message.');
    } finally {
      setIsSendingMsg(false);
    }
  };

  const handleUpdateStudentStage = async (student: any, newStage: string) => {
    try {
      setError(null);
      setSuccess(null);
      
      const parts = student.studentName.split(' ');
      const firstName = parts[0] || '';
      const lastName = parts.slice(1).join(' ') || '';

      const payload = {
        firstName,
        lastName,
        phoneNumber: student.phoneNumber || '',
        licenseNumber: student.licenseNumber || '',
        licenseExpiryDate: student.licenseExpiryDate ? student.licenseExpiryDate.split('T')[0] : null,
        theoryTestPassDate: student.theoryTestPassDate ? student.theoryTestPassDate.split('T')[0] : null,
        theoryTestPassReference: student.theoryTestPassReference || '',
        dvlaShareCode: student.dvlaShareCode || '',
        eyesightTestPassed: student.eyesightTestPassed ?? false,
        emergencyContactName: student.emergencyContactName || '',
        emergencyContactPhone: student.emergencyContactPhone || '',
        medicalConditions: student.medicalConditions || '',
        transmissionType: student.transmissionType || 'Manual',
        trainingStage: newStage
      };

      await api.updateStudentDetails(student.studentId, payload);
      setSuccess(`Updated stage of ${student.studentName} to ${newStage.replace(/([A-Z])/g, ' $1').trim()}.`);
      await fetchData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error updating training stage:', err);
      setError(err.response?.data?.message || 'Failed to update student stage.');
      setTimeout(() => setError(null), 5000);
    }
  };

  const eventStyleGetter = (event: any) => {
    let backgroundColor = '#1976d2'; // Default blue
    let border = 'none';

    if (event.status === 'Available') {
      backgroundColor = '#2e7d32'; // Green for available
    } else if (event.status === 'Pending') {
      backgroundColor = '#ed6c02'; // Orange/warning for pending
    } else if (event.status === 'Completed') {
      backgroundColor = '#757575'; // Grey for completed
    } else if (event.status === 'Approved') {
      backgroundColor = '#1565c0'; // Blue for approved/booked
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '6px',
        opacity: 0.9,
        color: 'white',
        border,
        display: 'block',
        fontSize: '0.85rem',
        fontWeight: 'bold',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }
    };
  };

  if (isSubscriptionLoading || isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
          Instructor Dashboard
        </Typography>
        <Box>
          <Button 
            variant="outlined" 
            color="primary"
            onClick={() => navigate('/instructor/subscribe')}
            sx={{ mr: 2, borderRadius: 2 }}
          >
            Manage Subscription
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => navigate('/instructor/availability/add')}
            sx={{ borderRadius: 2, fontWeight: 'bold' }}
          >
            Add Availability
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{success}</Alert>
      )}

      {/* Stats Cards Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.03)', border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(25, 118, 210, 0.08)', color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                <PeopleIcon />
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'medium' }}>Total Students</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e' }}>{stats.totalStudents}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.03)', border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(237, 108, 2, 0.08)', color: 'warning.main', display: 'flex', alignItems: 'center' }}>
                <TimerIcon />
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'medium' }}>Lesson Hours</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e' }}>{stats.totalHours} Hours</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.03)', border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(46, 125, 50, 0.08)', color: 'success.main', display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon />
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'medium' }}>Estimated Earnings</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>£{stats.earnings}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.03)', border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(211, 47, 47, 0.08)', color: 'error.main', display: 'flex', alignItems: 'center' }}>
                <RequestsIcon />
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'medium' }}>Pending Requests</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>{stats.pendingRequests}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={value} 
            onChange={handleChange} 
            aria-label="instructor dashboard tabs"
            variant="fullWidth"
          >
            <Tab label="Calendar" icon={<CalendarIcon />} {...a11yProps(0)} />
            <Tab label="Students" icon={<PeopleIcon />} {...a11yProps(1)} />
            <Tab 
              label={
                <Badge badgeContent={lessonRequests.length} color="error" variant="standard">
                  <Box component="span" sx={{ px: 1 }}>Lesson Requests</Box>
                </Badge>
              } 
              icon={<RequestsIcon />} 
              {...a11yProps(2)} 
            />
            <Tab 
              label={
                <Badge badgeContent={quotes.filter(q => q.status === 'Pending').length} color="warning" variant="standard">
                  <Box component="span" sx={{ px: 1 }}>Requests</Box>
                </Badge>
              } 
              icon={<MessageIcon />} 
              {...a11yProps(3)} 
            />
            <Tab label="Directory Profile" icon={<CampaignIcon />} {...a11yProps(4)} />
            <Tab label="Expenses" icon={<ReceiptIcon />} {...a11yProps(5)} />
            <Tab label="Student Kanban" icon={<KanbanIcon />} {...a11yProps(6)} />
            <Tab label="Referral & Feedback" icon={<CampaignIcon />} {...a11yProps(7)} />
            <Tab label="Test Routes" icon={<MapIcon />} {...a11yProps(8)} />
          </Tabs>
        </Box>
        
        <TabPanel value={value} index={0}>
          {/* Google Calendar sync settings banner */}
          <Paper sx={{ p: 2, mb: 3, borderRadius: 3, border: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" color="#1a237e" display="flex" alignItems="center" gap={1}>
                📅 Google Calendar Integration
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {googleCalendarEmail 
                  ? `Connected Account: ${googleCalendarEmail}` 
                  : 'Automatically sync your lessons and availability slots to your personal Google Calendar.'}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              {googleCalendarEmail && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={googleCalendarSyncEnabled}
                      onChange={(e) => handleToggleCalendarSync(e.target.checked)}
                      disabled={calendarSyncLoading}
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight="bold">
                      Enable Sync
                    </Typography>
                  }
                />
              )}
              <Button
                variant={googleCalendarEmail ? 'outlined' : 'contained'}
                color="primary"
                onClick={handleConnectCalendar}
                disabled={calendarSyncLoading}
                sx={{ fontWeight: 'bold', borderRadius: 2 }}
              >
                {calendarSyncLoading ? (
                  <CircularProgress size={20} />
                ) : googleCalendarEmail ? (
                  'Reconnect Account'
                ) : (
                  'Connect Google Calendar'
                )}
              </Button>
            </Box>
          </Paper>

          <Box sx={{ height: '70vh' }}>
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%' }}
              selectable
              view={calendarView}
              onView={(v) => setCalendarView(v)}
              date={calendarDate}
              onNavigate={(d) => setCalendarDate(d)}
              views={['month', 'week', 'day']}
              step={30}
              timeslots={2}
              eventPropGetter={eventStyleGetter}
              culture="en-US"
            />
          </Box>
        </TabPanel>
        
        <TabPanel value={value} index={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} mt={1} flexWrap="wrap" gap={2}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#37474f' }}>
              My Students and Progress Status
            </Typography>
            <Box display="flex" gap={1.5}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<CampaignIcon />}
                onClick={() => handleOpenMessageDialog()}
                sx={{ borderRadius: 2, fontWeight: 'bold', textTransform: 'none' }}
              >
                Send Group Announcement
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => navigate('/instructor/students')}
                sx={{ borderRadius: 2, fontWeight: 'bold', textTransform: 'none' }}
              >
                Student Registration & Management
              </Button>
            </Box>
          </Box>
          
          {students.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#fcfcfc', borderRadius: 3, border: '1px solid #e0e0e0' }}>
              <PeopleIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#37474f' }}>
                No Students Found
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                You do not have any registered or assigned students yet.
              </Typography>
            </Paper>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.04)', border: '1px solid #e0e0e0' }}>
                <Table>
                  <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Student Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Student Number</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Transmission Type</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Completed Lessons</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Syllabus Progress (DVSA)</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {students.map((student) => (
                      <TableRow key={student.studentId} hover>
                        <TableCell sx={{ fontWeight: 'semibold' }}>{student.studentName}</TableCell>
                        <TableCell>{student.studentNumber || '-'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={student.transmissionType === 'Automatic' ? 'Automatic' : 'Manual'} 
                            color={student.transmissionType === 'Automatic' ? 'secondary' : 'default'}
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {student.completedLessons} / {student.totalLessons}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ width: '30%' }}>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Box sx={{ width: '100%' }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={((student.coveredDvsaTopicsCount || 0) / 19) * 100} 
                                sx={{ 
                                  height: 6, 
                                  borderRadius: 3, 
                                  backgroundColor: '#eceff1',
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: '#4caf50'
                                  }
                                }}
                              />
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 45 }}>
                              {student.coveredDvsaTopicsCount || 0}/19
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Box display="flex" justifyContent="center" gap={1}>
                            <Button
                              variant="outlined"
                              color="secondary"
                              size="small"
                              startIcon={<MessageIcon />}
                              onClick={() => handleOpenMessageDialog(student.studentId)}
                              sx={{ borderRadius: 1.5, textTransform: 'none' }}
                            >
                              Message
                            </Button>
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              onClick={() => navigate('/instructor/students')}
                              sx={{ borderRadius: 1.5, textTransform: 'none' }}
                            >
                              Details & Manage
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={value} index={2}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2, color: '#37474f' }}>
            Pending Lesson Requests
          </Typography>
          
          {lessonRequests.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, backgroundColor: '#fcfcfc' }}>
              <Typography variant="body1" color="textSecondary">
                There are currently no lesson requests pending approval.
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}>
              <Table>
                <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Student</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Date / Time</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Duration</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Notes</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lessonRequests.map((req) => {
                    const date = new Date(req.lessonDate);
                    return (
                      <TableRow key={req.id} hover>
                        <TableCell sx={{ fontWeight: 'medium' }}>{req.studentName}</TableCell>
                        <TableCell>{format(date, 'dd.MM.yyyy - HH:mm')}</TableCell>
                        <TableCell>
                          <Chip 
                            label={`${req.duration} Minutes`} 
                            size="small" 
                            variant="outlined" 
                            color="primary" 
                            sx={{ fontWeight: 'bold' }} 
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 250, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {req.notes || <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>No notes</Typography>}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Box display="flex" justifyContent="center" gap={1}>
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              startIcon={<CheckIcon />}
                              onClick={() => handleApproveRequest(req.id)}
                              disabled={actionInProgress !== null}
                              sx={{ borderRadius: 2, fontWeight: 'bold' }}
                            >
                              Approve
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              startIcon={<CancelIcon />}
                              onClick={() => handleRejectRequest(req.id)}
                              disabled={actionInProgress !== null}
                              sx={{ borderRadius: 2, fontWeight: 'bold' }}
                            >
                              Reject
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={value} index={3}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2, color: '#37474f' }}>
            Requests from Students
          </Typography>
          
          {quotes.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, backgroundColor: '#fcfcfc' }}>
              <Typography variant="body1" color="textSecondary">
                You have not received any requests yet.
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.04)', overflowX: 'auto' }}>
              <Table sx={{ minWidth: 800 }}>
                <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Student</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Preference</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Experience</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Message</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions / Reply</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {quotes.map((quote) => {
                    const date = new Date(quote.createdAt);
                    return (
                      <TableRow key={quote.id} hover>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            {quote.studentName}
                          </Typography>
                          {quote.status === 'Accepted' && (
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                {quote.studentEmail}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                {quote.studentPhone || 'No Phone'}
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={quote.preferredTransmission} 
                            size="small" 
                            color={quote.preferredTransmission === 'Automatic' ? 'secondary' : 'primary'}
                            variant="outlined" 
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={quote.experienceLevel} 
                            size="small" 
                            variant="outlined" 
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {quote.message}
                        </TableCell>
                        <TableCell>{format(date, 'dd.MM.yyyy - HH:mm')}</TableCell>
                        <TableCell>
                          <Chip 
                            label={quote.status} 
                            color={quote.status === 'Accepted' ? 'success' : quote.status === 'Rejected' ? 'error' : 'warning'}
                            size="small"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', minWidth: 180 }}>
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            {quote.status === 'Pending' ? (
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => {
                                  setSelectedQuote(quote);
                                  setReplyStatus('Accepted');
                                  setQuoteReplyMessage('');
                                  setIsQuoteReplyOpen(true);
                                }}
                                sx={{ borderRadius: 2, fontWeight: 'bold' }}
                              >
                                Respond
                              </Button>
                            ) : (
                              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', maxWidth: 150, wordBreak: 'normal' }}>
                                Reply: {quote.responseMessage || 'None'}
                              </Typography>
                            )}
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => handleDeleteQuote(quote.id)}
                              title="Delete Request"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={value} index={4}>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.04)', border: '1px solid #e0e0e0' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3, color: '#1a237e' }}>
                    Directory Profile Details
                  </Typography>
                  <form onSubmit={handleUpdateProfile}>
                    <Box display="flex" flexDirection="column" gap={3}>
                      {/* Profile Image and Company Logo Section */}
                      <Grid container spacing={3} sx={{ mb: 2 }}>
                        {/* Profile Picture */}
                        <Grid size={{ xs: 12, sm: 6 }} display="flex" flexDirection="column" alignItems="center">
                          <Box sx={{ position: 'relative', width: 120, height: 120, mb: 1.5 }}>
                            <Avatar
                              src={profileImageUrl ? getImageUrl(profileImageUrl) : undefined}
                              sx={{ width: 120, height: 120, border: '4px solid #1a237e', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', bgcolor: 'primary.main', fontSize: '2.5rem', fontWeight: 'bold' }}
                            >
                              {getInitials() || <PersonIcon sx={{ fontSize: 60 }} />}
                            </Avatar>
                            <input
                              accept="image/*"
                              style={{ display: 'none' }}
                              id="profile-image-upload"
                              type="file"
                              disabled={isUploadingProfileImage}
                              onChange={handleProfileImageUpload}
                            />
                            <label htmlFor="profile-image-upload">
                              <IconButton
                                component="span"
                                disabled={isUploadingProfileImage}
                                sx={{
                                  position: 'absolute',
                                  bottom: 0,
                                  right: 0,
                                  backgroundColor: '#1a237e',
                                  color: '#fff',
                                  '&:hover': { backgroundColor: '#0d47a1' },
                                  border: '2px solid #fff',
                                  p: 1
                                }}
                              >
                                {isUploadingProfileImage ? <CircularProgress size={18} color="inherit" /> : <UploadIcon sx={{ fontSize: 18 }} />}
                              </IconButton>
                            </label>
                          </Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Profile Picture</Typography>
                          <Typography variant="caption" color="textSecondary" align="center">
                            Shown on instructor list (Max: 5MB)
                          </Typography>
                        </Grid>

                        {/* Company Logo */}
                        <Grid size={{ xs: 12, sm: 6 }} display="flex" flexDirection="column" alignItems="center">
                          <Box sx={{ position: 'relative', width: 120, height: 120, mb: 1.5 }}>
                            <Avatar
                              variant="rounded"
                              src={logoUrl ? getImageUrl(logoUrl) : undefined}
                              sx={{ width: 120, height: 120, border: '4px solid #1a237e', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', bgcolor: '#fff', fontSize: '2.5rem', fontWeight: 'bold', color: '#1a237e' }}
                            >
                              {!logoUrl && <BusinessIcon sx={{ fontSize: 60, color: '#bdbdbd' }} />}
                            </Avatar>
                            <input
                              accept="image/*"
                              style={{ display: 'none' }}
                              id="logo-image-upload"
                              type="file"
                              disabled={isUploadingLogo}
                              onChange={handleLogoUpload}
                            />
                            <label htmlFor="logo-image-upload">
                              <IconButton
                                component="span"
                                disabled={isUploadingLogo}
                                sx={{
                                  position: 'absolute',
                                  bottom: 0,
                                  right: 0,
                                  backgroundColor: '#1a237e',
                                  color: '#fff',
                                  '&:hover': { backgroundColor: '#0d47a1' },
                                  border: '2px solid #fff',
                                  p: 1
                                }}
                              >
                                {isUploadingLogo ? <CircularProgress size={18} color="inherit" /> : <UploadIcon sx={{ fontSize: 18 }} />}
                              </IconButton>
                            </label>
                          </Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Company Logo</Typography>
                          <Typography variant="caption" color="textSecondary" align="center">
                            Shown in search and student pages (Max: 5MB)
                          </Typography>
                        </Grid>
                      </Grid>

                      <TextField
                        label="Profile Bio / Introduction"
                        multiline
                        rows={4}
                        placeholder="Introduce yourself to prospective students, mention your experience, teaching style..."
                        value={profileBio}
                        onChange={(e) => setProfileBio(e.target.value)}
                        fullWidth
                      />

                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField
                            label="Base Hourly Rate (£)"
                            type="number"
                            inputProps={{ min: 10, max: 150, step: 0.5 }}
                            value={profileRate}
                            onChange={(e) => setProfileRate(parseFloat(e.target.value) || 40)}
                            fullWidth
                            required
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <FormControl fullWidth required>
                            <InputLabel id="profile-transmission-label">Transmission Type</InputLabel>
                            <Select
                              labelId="profile-transmission-label"
                              value={profileTransmission}
                              onChange={(e) => setProfileTransmission(e.target.value)}
                              label="Transmission Type"
                            >
                              <MenuItem value="Manual">Manual Only</MenuItem>
                              <MenuItem value="Automatic">Automatic Only</MenuItem>
                              <MenuItem value="Both">Both (Manual & Automatic)</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <FormControl fullWidth>
                            <InputLabel id="profile-gender-label">Gender</InputLabel>
                            <Select
                              labelId="profile-gender-label"
                              value={profileGender}
                              onChange={(e) => setProfileGender(e.target.value)}
                              label="Gender"
                            >
                              <MenuItem value="">Prefer Not to Say / Other</MenuItem>
                              <MenuItem value="Male">Male</MenuItem>
                              <MenuItem value="Female">Female</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>

                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#37474f' }}>
                          Served Cities & Districts
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                          Select the cities and districts where you offer driving instruction. Only students searching in these locations can find you.
                        </Typography>
                        
                        <Box sx={{ p: 2.5, border: '1px solid #e2e8f0', borderRadius: 3, bgcolor: '#f8fafc' }}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid size={{ xs: 12, sm: 5 }}>
                              <FormControl fullWidth size="small">
                                <InputLabel id="select-city-label">Select City</InputLabel>
                                <Select
                                  labelId="select-city-label"
                                  label="Select City"
                                  value={selectedCity}
                                  onChange={(e) => {
                                    setSelectedCity(e.target.value);
                                    setSelectedDistrict('');
                                  }}
                                  sx={{ bgcolor: '#fff' }}
                                >
                                  <MenuItem value=""><em>None</em></MenuItem>
                                  {REGION_GROUPS.map((g) => (
                                    <MenuItem key={g.name} value={g.name}>{g.name}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 5 }}>
                              <FormControl fullWidth size="small" disabled={!selectedCity}>
                                <InputLabel id="select-district-label">Select District</InputLabel>
                                <Select
                                  labelId="select-district-label"
                                  label="Select District"
                                  value={selectedDistrict}
                                  onChange={(e) => setSelectedDistrict(e.target.value)}
                                  sx={{ bgcolor: '#fff' }}
                                >
                                  <MenuItem value=""><em>None</em></MenuItem>
                                  {(REGION_GROUPS.find((g) => g.name === selectedCity)?.outcodes || [])
                                    .filter((o) => !profileOutcodes.includes(o.code))
                                    .map((o) => (
                                      <MenuItem key={o.code} value={o.code}>{o.area.split(',')[0]}</MenuItem>
                                    ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 2 }}>
                              <Button
                                fullWidth
                                variant="contained"
                                color="primary"
                                onClick={handleAddDistrict}
                                disabled={!selectedDistrict}
                                sx={{ height: '40px', fontWeight: 'bold', textTransform: 'none' }}
                              >
                                Add Area
                              </Button>
                            </Grid>
                          </Grid>

                          <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {profileOutcodes.length === 0 ? (
                              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', width: '100%', py: 1, textAlign: 'center' }}>
                                No served districts selected yet. Please select a city and district above to add.
                              </Typography>
                            ) : (
                              profileOutcodes.map((code) => (
                                <Chip
                                  key={code}
                                  label={getOutcodeAreaName(code)}
                                  onDelete={() => handleRemoveDistrict(code)}
                                  color="primary"
                                  variant="outlined"
                                  sx={{ fontWeight: '500', borderRadius: 2, bgcolor: '#fff' }}
                                />
                              ))
                            )}
                          </Box>
                        </Box>
                      </Box>

                      <Box sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#37474f' }}>
                          License Training Categories
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                          Select the categories of vehicles you offer training for.
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, bgcolor: '#fafafa' }}>
                          <Grid container spacing={2}>
                            {LICENSE_CATEGORIES.map((cat) => {
                              const isChecked = profileLicenseTypes.includes(cat.code);
                              return (
                                <Grid size={{ xs: 12, sm: 6 }} key={cat.code}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={isChecked}
                                        onChange={() => handleLicenseToggle(cat.code)}
                                        color="secondary"
                                        size="small"
                                      />
                                    }
                                    label={
                                      <Typography variant="body2" sx={{ fontWeight: 'semibold', fontSize: '0.85rem' }}>
                                        {cat.name}
                                      </Typography>
                                    }
                                  />
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Paper>
                      </Box>

                      <Box sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#37474f' }}>
                          Vehicle Photo Gallery ({profileVehicleImageUrls.length}/3)
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                          Upload up to 3 photos of your training vehicle. These will be shown on your public profile card.
                        </Typography>
                        
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          {profileVehicleImageUrls.map((url, idx) => (
                            <Grid size={{ xs: 4 }} key={idx}>
                              <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0', pt: '75%', bgcolor: '#f5f5f5' }}>
                                <Box 
                                  component="img"
                                  src={getImageUrl(url)}
                                  alt={`Vehicle ${idx + 1}`}
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                />
                                <Button
                                  variant="contained"
                                  color="error"
                                  size="small"
                                  onClick={() => handleDeleteVehicleImage(idx)}
                                  sx={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    minWidth: 0,
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    p: 0,
                                    fontSize: '0.75rem',
                                    fontWeight: 'bold',
                                    lineHeight: 1
                                  }}
                                >
                                  X
                                </Button>
                              </Box>
                            </Grid>
                          ))}
                          {profileVehicleImageUrls.length < 3 && (
                            <Grid size={{ xs: 4 }}>
                              <Box 
                                sx={{ 
                                  borderRadius: 2, 
                                  border: '2px dashed #ccc', 
                                  pt: '75%', 
                                  position: 'relative',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(25, 118, 210, 0.02)' }
                                }}
                                component="label"
                              >
                                <input
                                  type="file"
                                  accept="image/*"
                                  hidden
                                  disabled={isUploadingVehicleImage}
                                  onChange={handleVehicleImageUpload}
                                />
                                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                  {isUploadingVehicleImage ? (
                                    <CircularProgress size={24} />
                                  ) : (
                                    <>
                                      <Typography variant="h5" color="textSecondary" sx={{ mb: 0.5 }}>+</Typography>
                                      <Typography variant="caption" color="textSecondary">Upload</Typography>
                                    </>
                                  )}
                                </Box>
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </Box>

                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isUpdatingProfile}
                        sx={{ mt: 2, py: 1.5, fontWeight: 'bold', borderRadius: 2 }}
                      >
                        {isUpdatingProfile ? <CircularProgress size={24} color="inherit" /> : 'Save Profile Changes'}
                      </Button>
                    </Box>
                  </form>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.04)', border: '1px solid #e0e0e0', bgcolor: 'rgba(25, 118, 210, 0.01)' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1a237e', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CampaignIcon color="primary" /> Boost Directory Profile
                  </Typography>
                  
                  {isFeatured ? (
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(46, 125, 50, 0.08)', borderRadius: 2, border: '1px solid rgba(46, 125, 50, 0.2)' }}>
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckIcon /> Featured Status Active!
                      </Typography>
                      {featuredUntil && (
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                          Your profile will rank at the top of results until: <strong>{format(new Date(featuredUntil), 'dd.MM.yyyy - HH:mm')}</strong>
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                      Get up to 5x more lesson inquiries by boosting your profile. Featured instructors always appear at the top of search listings with a highlighted badge.
                    </Typography>
                  )}

                  <Box display="flex" flexDirection="column" gap={3}>
                    {/* Pack 1 */}
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2.5, 
                        borderRadius: 3, 
                        border: '1px solid #e0e0e0',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        transition: 'box-shadow 0.3s',
                        '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          7-Day Booster Pack
                        </Typography>
                        <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'extrabold' }}>
                          £5.00
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        Feature your driving school profile for 7 days. Perfect for filling last-minute slots.
                      </Typography>
                      <Button
                        variant="outlined"
                        color="primary"
                        disabled={boosting}
                        onClick={() => handleBoostProfile(7, 5.00)}
                        sx={{ fontWeight: 'bold', borderRadius: 2, textTransform: 'none' }}
                      >
                        {boosting ? <CircularProgress size={20} /> : 'Boost Profile via PayPal'}
                      </Button>
                    </Paper>

                    {/* Pack 2 */}
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2.5, 
                        borderRadius: 3, 
                        border: '2px solid #1976d2',
                        bgcolor: 'rgba(25, 118, 210, 0.02)',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        transition: 'box-shadow 0.3s',
                        '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }
                      }}
                    >
                      <Box sx={{ position: 'absolute', top: -12, right: 16, bgcolor: '#1976d2', color: 'white', px: 1.5, py: 0.2, borderRadius: 10, fontSize: '0.65rem', fontWeight: 'bold', textTransform: 'uppercase' }}>
                        Best Value
                      </Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          30-Day Premium Boost
                        </Typography>
                        <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'extrabold' }}>
                          £15.00
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        Feature your profile for a full month. Maintain a steady stream of new student bookings.
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={boosting}
                        onClick={() => handleBoostProfile(30, 15.00)}
                        sx={{ fontWeight: 'bold', borderRadius: 2, textTransform: 'none' }}
                      >
                        {boosting ? <CircularProgress size={20} color="inherit" /> : 'Boost Profile via PayPal'}
                      </Button>
                    </Paper>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={value} index={5}>
          <ExpenseTracker />
        </TabPanel>

        <TabPanel value={value} index={6}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} mt={1}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Student Progress Stage Tracking Board
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {STAGES.map((stage) => {
              const stageStudents = students.filter(
                (s) => (s.trainingStage || 'NewStarter') === stage.id
              );

              return (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={stage.id} sx={{ display: 'flex' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: theme.palette.mode === 'dark' ? stage.darkBg : stage.lightBg,
                      height: '100%',
                      minHeight: '60vh',
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                      sx={{
                        borderBottom: '3px solid',
                        borderColor: stage.color,
                        pb: 1,
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: stage.color }}>
                        {stage.label}
                      </Typography>
                      <Chip
                        label={stageStudents.length}
                        size="small"
                        sx={{
                          fontWeight: 'bold',
                          backgroundColor: stage.color,
                          color: '#fff',
                        }}
                      />
                    </Box>

                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={1.5}
                      sx={{
                        flexGrow: 1,
                        overflowY: 'auto',
                      }}
                    >
                      {stageStudents.length === 0 ? (
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          sx={{
                            flexGrow: 1,
                            border: '1px dashed',
                            borderColor: 'divider',
                            borderRadius: 2,
                            p: 3,
                            textAlign: 'center',
                            minHeight: 150
                          }}
                        >
                          <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                            No students in this stage
                          </Typography>
                        </Box>
                      ) : (
                        stageStudents.map((student) => {
                          const stageIndex = STAGES.findIndex((st) => st.id === (student.trainingStage || 'NewStarter'));
                          const progressVal = ((student.coveredDvsaTopicsCount || 0) / 19) * 100;

                          return (
                            <Card
                              key={student.studentId}
                              sx={{
                                borderRadius: 2,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                border: '1px solid',
                                borderColor: 'divider',
                                '&:hover': {
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                },
                              }}
                            >
                              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}>
                                  {student.studentName}
                                </Typography>

                                <Box display="flex" gap={0.5} flexWrap="wrap" mb={1.5}>
                                  <Chip
                                    label={student.transmissionType || 'Manual'}
                                    size="small"
                                    color={student.transmissionType === 'Automatic' ? 'secondary' : 'default'}
                                    variant="outlined"
                                    sx={{ fontSize: '0.65rem', height: 20 }}
                                  />
                                  {student.theoryTestPassDate && (
                                    <Chip
                                      label="Theory Pass"
                                      size="small"
                                      color="success"
                                      variant="filled"
                                      sx={{ fontSize: '0.65rem', height: 20 }}
                                    />
                                  )}
                                </Box>

                                <Box sx={{ width: '100%', mb: 1 }}>
                                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                                    <Typography variant="caption" color="textSecondary">
                                      Syllabus Progress
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                      {student.coveredDvsaTopicsCount || 0}/19
                                    </Typography>
                                  </Box>
                                  <LinearProgress
                                    variant="determinate"
                                    value={progressVal}
                                    sx={{
                                      height: 6,
                                      borderRadius: 3,
                                      backgroundColor: 'action.hover',
                                      '& .MuiLinearProgress-bar': {
                                        borderRadius: 3,
                                      },
                                    }}
                                  />
                                </Box>

                                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                                  <IconButton
                                    size="small"
                                    disabled={stageIndex === 0}
                                    onClick={() => handleUpdateStudentStage(student, STAGES[stageIndex - 1].id)}
                                    color="primary"
                                    sx={{ border: '1px solid', borderColor: 'divider' }}
                                  >
                                    <ArrowBackIcon fontSize="small" />
                                  </IconButton>
                                  <FormControl size="small" variant="standard" sx={{ minWidth: 80 }}>
                                    <Select
                                      value={student.trainingStage || 'NewStarter'}
                                      onChange={(e) => handleUpdateStudentStage(student, e.target.value as string)}
                                      sx={{ fontSize: '0.7rem' }}
                                      disableUnderline
                                    >
                                      {STAGES.map((s) => (
                                        <MenuItem key={s.id} value={s.id} sx={{ fontSize: '0.75rem' }}>
                                          {s.label}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  <IconButton
                                    size="small"
                                    disabled={stageIndex === STAGES.length - 1}
                                    onClick={() => handleUpdateStudentStage(student, STAGES[stageIndex + 1].id)}
                                    color="primary"
                                    sx={{ border: '1px solid', borderColor: 'divider' }}
                                  >
                                    <ArrowForwardIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </CardContent>
                            </Card>
                          );
                        })
                      )}
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </TabPanel>

        <TabPanel value={value} index={7}>
          <ReferralAndReviews />
        </TabPanel>

        <TabPanel value={value} index={8}>
          <TestRoutesTab isInstructor={true} />
        </TabPanel>
      </Box>
      {/* Send Message & Announcement Dialog */}
      <Dialog
        open={isMsgDialogOpen}
        onClose={() => setIsMsgDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <form onSubmit={handleSendMessage}>
          <DialogTitle sx={{ fontWeight: 'bold', color: '#1a237e', display: 'flex', alignItems: 'center', gap: 1 }}>
            <MessageIcon color="primary" /> {msgData.studentId ? 'Send Message to Student' : 'Send Announcement to All Students'}
          </DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={2.5} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel id="msg-student-label">Recipient</InputLabel>
                <Select
                  labelId="msg-student-label"
                  value={msgData.studentId}
                  onChange={(e) => setMsgData(prev => ({ ...prev, studentId: e.target.value }))}
                  label="Recipient"
                  disabled={!!msgData.studentId}
                >
                  <MenuItem value=""><em>All Students (Group Announcement)</em></MenuItem>
                  {students.map((s) => (
                    <MenuItem key={s.studentId} value={s.studentId}>{s.studentName}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Message Content"
                required
                fullWidth
                multiline
                rows={4}
                placeholder="Write the message you want to send to your student or class..."
                value={msgData.message}
                onChange={(e) => setMsgData(prev => ({ ...prev, message: e.target.value }))}
              />

              <Box display="flex" flexDirection="column" gap={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={msgData.sendEmail}
                      onChange={(e) => setMsgData(prev => ({ ...prev, sendEmail: e.target.checked }))}
                    />
                  }
                  label="Also send as email (Simulated)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={msgData.sendSms}
                      onChange={(e) => setMsgData(prev => ({ ...prev, sendSms: e.target.checked }))}
                    />
                  }
                  label="Also send as SMS (Simulated)"
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setIsMsgDialogOpen(false)} color="inherit" disabled={isSendingMsg}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={isSendingMsg}>
              {isSendingMsg ? <CircularProgress size={24} color="inherit" /> : 'Send'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Quote Reply Dialog */}
      <Dialog
        open={isQuoteReplyOpen}
        onClose={() => setIsQuoteReplyOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <form onSubmit={handleRespondToQuote}>
          <DialogTitle sx={{ fontWeight: 'bold', color: '#1a237e' }}>
            Respond to Request
          </DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={2.5} sx={{ mt: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Student Name: <strong>{selectedQuote?.studentName}</strong>
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Request message: <em>"{selectedQuote?.message}"</em>
              </Typography>
              
              <FormControl fullWidth>
                <InputLabel id="quote-status-label">Action</InputLabel>
                <Select
                  labelId="quote-status-label"
                  value={replyStatus}
                  onChange={(e) => setReplyStatus(e.target.value as 'Accepted' | 'Rejected')}
                  label="Action"
                >
                  <MenuItem value="Accepted">Accept & Link Student</MenuItem>
                  <MenuItem value="Rejected">Reject Request</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Reply Message"
                required
                fullWidth
                multiline
                rows={3}
                placeholder="Write a message to the student (e.g. lesson scheduling details or pricing info)..."
                value={quoteReplyMessage}
                onChange={(e) => setQuoteReplyMessage(e.target.value)}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setIsQuoteReplyOpen(false)} color="inherit" disabled={isSubmittingReply}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color={replyStatus === 'Accepted' ? 'success' : 'error'} disabled={isSubmittingReply}>
              {isSubmittingReply ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default InstructorDashboard;
